import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "./api";
import { Karte, Laedt } from "./ui";

/**
 * Der Verlauf der Messwerte eines Objekts: Wasser, Chlor, pH.
 *
 * Die Datei kommt aus dem Eigentuemerportal und ist beim Wegfall des Portals
 * hierher gezogen, statt geloescht zu werden. Der Grund ist nicht Sparsamkeit:
 * das Buero braucht diesen Verlauf haeufiger als der Eigentuemer je brauchte.
 * Wer am Telefon erklaeren soll, warum eine Wasserrechnung hoeher ausfaellt,
 * braucht die letzten zwoelf Ablesungen und nicht den heutigen Stand.
 *
 * Zwei kleine Diagramme, gezeichnet als SVG.
 *
 * Kein Diagrammpaket: die beiden Formen hier sind zusammen keine zweihundert
 * Zeilen, waehrend eine Bibliothek die Anwendung um ein Vielfaches davon
 * vergroessert. Auf einem Telefon im mallorquinischen Funkloch ist jedes
 * eingesparte Paket ein Ladevorgang weniger.
 *
 * Gestaltungsregeln, an die sich beide halten:
 *
 *   Duenne Marken, zurueckhaltende Achsen, keine Zahl an jedem Punkt. Eine
 *   Legende gibt es, seit die Farben etwas bedeuten: Zielband, Vergleichslinie
 *   und rote Marken sind Aussagen, und eine Aussage ohne Beschriftung ist
 *   Dekoration.
 *   Die Akzentfarbe traegt gegen Weiss zu wenig Kontrast, um allein zu stehen.
 *   Deshalb hat jede Karte ihren letzten Wert als Zahl und darunter eine
 *   ausklappbare Tabelle. Wer die Farbe nicht sieht, verliert nichts.
 */

/*
 * Die Farben kommen aus den Merkmalen der Marke und stehen nicht mehr als
 * feste Werte im Quelltext.
 *
 * Grund: das Markenkit eines Kunden wird spaeter als Datensatz geladen und
 * setzt genau diese Merkmale. Stuenden die Werte hier fest, waere das Diagramm
 * die eine Stelle der Anwendung, die im Kundenbetrieb weiterhin in fremden
 * Farben leuchtet. Ein Ausweichwert steht daneben, damit die Zeichnung auch
 * dann etwas zeigt, wenn ein Merkmal fehlt.
 */
const AKZENT = "var(--color-marke, #f5822b)";
const FLAECHE = "var(--color-grund, #f1f5f9)";
const LEISE = "var(--color-leise, #5b6b82)";
const ROT = "var(--color-fehler, #d8443c)";

export interface Punkt {
  datum: string;
  wert: number;
  ausserhalb?: boolean;
  tage?: number;
  menge?: number;
}

export interface Reihe {
  key: string;
  art: "verbrauch" | "wert";
  label: Record<string, string>;
  einheit: string | null;
  ziel: { min: number | null; max: number | null } | null;
  punkte: Punkt[];
  kennzahlen?: {
    proTag: number | null;
    monatProTag: number | null;
    vormonatProTag: number | null;
    letzteWoche: number | null;
    basis: number | null;
    auffaellig: boolean;
    tageImAbschnitt: number | null;
    leer: boolean | null;
    grundlast: number | null;
    leckverdacht: boolean;
    vergleich: "leer" | "belegt" | "alle";
  };
}

const BREITE = 320;
const HOEHE = 120;
const RAND = { oben: 8, unten: 18, links: 4, rechts: 4 };

/**
 * Die Karte am Objekt.
 *
 * Sie laedt selbst und zeigt nichts, wenn es nichts zu zeigen gibt. Eine leere
 * Karte mit der Ueberschrift "Zahlen aus den Berichten" waere auf jeder zweiten
 * Objektseite eine Zeile Platzverschwendung: viele Objekte haben weder Pool
 * noch abgelesenen Zaehler.
 */
export function ZahlenAmObjekt({ propertyId }: { propertyId: string }) {
  const { t } = useTranslation();
  const [reihen, setReihen] = useState<Reihe[] | null>(null);

  useEffect(() => {
    void api.get<{ reihen: Reihe[] }>(`/properties/${propertyId}/reihen`)
      .then((r) => setReihen(r.reihen))
      .catch(() => setReihen([]));
  }, [propertyId]);

  if (!reihen) {
    return <Karte className="lg:col-span-2"><Laedt /></Karte>;
  }

  const mitDaten = reihen.filter((r) => r.punkte.length > 0);
  if (mitDaten.length === 0) return null;

  return (
    <Karte className="lg:col-span-2">
      <h2 className="mb-1 font-bold">{t("objektzahlen.titel")}</h2>
      <p className="mb-3 text-sm text-leise">{t("objektzahlen.hinweis")}</p>
      <div className="grid gap-4 md:grid-cols-2">
        {mitDaten.map((r) => <Diagrammkarte key={r.key} reihe={r} />)}
      </div>
    </Karte>
  );
}

export function Diagrammkarte({ reihe }: { reihe: Reihe }) {
  const { t, i18n } = useTranslation();
  const [zahlen, setZahlen] = useState(false);
  const sprache = i18n.language;

  const titel = reihe.label[sprache] || reihe.label.de || reihe.key;
  const letzter = reihe.punkte[reihe.punkte.length - 1];

  const datum = (d: string) => new Date(d).toLocaleDateString(sprache, {
    day: "2-digit", month: "2-digit"
  });

  return (
    <div className="rounded-card border border-linie p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="font-semibold">{titel}</h3>
        {letzter && (
          <span className="text-lg font-bold tabular-nums">
            {zahl(letzter.wert, sprache)}
            {reihe.einheit ? ` ${reihe.einheit}` : ""}
          </span>
        )}
      </div>
      <p className="text-xs text-leise">
        {reihe.art === "verbrauch" ? t("objektzahlen.jeTag") : t("objektzahlen.letzterWert")}
      </p>

      {/*
        * Die Zahlen, die das Bild nicht zeigen kann.
        *
        * Ein Verlauf sagt, dass sich etwas geaendert hat. Ob das viel ist, sagt
        * erst der Vergleich: dieser Monat gegen den vorigen, und die Woche bis
        * zur letzten Ablesung. Ohne sie ist die Kurve huebsch und ohne Aussage.
        */}
      {reihe.kennzahlen && (
        <dl className="mt-3 grid grid-cols-2 gap-2 rounded bg-grund/50 p-2 text-center sm:grid-cols-4">
          <Zahlfeld
            titel={t("objektzahlen.dieseWoche")}
            wert={reihe.kennzahlen.letzteWoche}
            einheit={reihe.einheit}
            sprache={sprache}
          />
          <Zahlfeld
            titel={t("objektzahlen.dieserMonat")}
            wert={reihe.kennzahlen.monatProTag}
            einheit={reihe.einheit ? `${reihe.einheit}/${t("objektzahlen.tag")}` : null}
            sprache={sprache}
          />
          {/*
            Was laeuft, wenn niemand da ist. Das ist die aufschlussreichste Zahl
            der ganzen Karte: Pool und Bewaesserung erklaeren sie, alles darueber
            erklaert sie nicht.
          */}
          <Zahlfeld
            titel={t("objektzahlen.grundlast")}
            wert={reihe.kennzahlen.grundlast}
            einheit={reihe.einheit ? `${reihe.einheit}/${t("objektzahlen.tag")}` : null}
            sprache={sprache}
          />
          <Zahlfeld
            titel={t("objektzahlen.vormonat")}
            wert={reihe.kennzahlen.vormonatProTag}
            einheit={reihe.einheit ? `${reihe.einheit}/${t("objektzahlen.tag")}` : null}
            sprache={sprache}
          />
        </dl>
      )}

      {/*
        Zwei verschiedene Saetze, weil es zwei verschiedene Nachrichten sind.
        Stand das Haus leer, ist der erhoehte Verbrauch ein Hinweis auf eine
        undichte Stelle. War jemand da, ist er zuerst die Erklaerung selbst,
        und ein Satz ueber ein moegliches Leck waere ein Schreck ohne Anlass.
      */}
      {reihe.kennzahlen?.leckverdacht && (
        <p className="mt-2 rounded border-s-4 border-red-700 bg-red-50 px-3 py-2 text-sm text-red-900">
          {t("objektzahlen.leckverdacht", {
            wert: zahl(reihe.kennzahlen.proTag ?? 0, sprache),
            basis: zahl(reihe.kennzahlen.basis ?? 0, sprache),
            einheit: reihe.einheit ?? ""
          })}
        </p>
      )}
      {reihe.kennzahlen?.auffaellig && !reihe.kennzahlen.leckverdacht && (
        <p className="mt-2 rounded border-s-4 border-marke bg-grund px-3 py-2 text-sm">
          {t("objektzahlen.auffaellig", {
            wert: zahl(reihe.kennzahlen.proTag ?? 0, sprache),
            basis: zahl(reihe.kennzahlen.basis ?? 0, sprache),
            einheit: reihe.einheit ?? ""
          })}
        </p>
      )}

      <div className="mt-3">
        {reihe.art === "verbrauch"
          ? <Balken
              punkte={reihe.punkte}
              basis={reihe.kennzahlen?.basis ?? null}
              sprache={sprache}
            />
          : <Linie reihe={reihe} sprache={sprache} />}
      </div>

      {/*
        * Die Legende.
        *
        * Seit ein Zielband, eine Vergleichslinie und rote Marken dazugekommen
        * sind, traegt die Farbe eine Aussage, und eine Aussage ohne
        * Beschriftung ist Dekoration.
        */}
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-leise">
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-4 rounded-sm" style={{ background: AKZENT }} />
          {reihe.art === "verbrauch"
            ? t("objektzahlen.legendeVerbrauch", { einheit: reihe.einheit ?? "" })
            : t("objektzahlen.legendeWert")}
        </li>
        {reihe.art === "verbrauch" && reihe.kennzahlen?.basis !== null
          && reihe.kennzahlen?.basis !== undefined && (
          <li className="flex items-center gap-1.5">
            <span className="inline-block h-0 w-4 border-t-2 border-dashed"
              style={{ borderColor: LEISE }} />
            {t("objektzahlen.legendeUeblich", {
              wert: zahl(reihe.kennzahlen.basis, sprache)
            })}
          </li>
        )}
        {reihe.art === "wert" && reihe.ziel
          && (reihe.ziel.min !== null || reihe.ziel.max !== null) && (
          <li className="flex items-center gap-1.5">
            <span className="inline-block h-3 w-4 rounded-sm" style={{ background: FLAECHE }} />
            {t("objektzahlen.legendeZiel", {
              min: reihe.ziel.min === null ? "—" : zahl(reihe.ziel.min, sprache),
              max: reihe.ziel.max === null ? "—" : zahl(reihe.ziel.max, sprache)
            })}
          </li>
        )}
        <li className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: ROT }} />
          {reihe.art === "verbrauch"
            ? t("objektzahlen.legendeMehr")
            : t("objektzahlen.legendeAusserhalb")}
        </li>
      </ul>

      <button
        type="button"
        onClick={() => setZahlen((z) => !z)}
        className="mt-2 text-xs text-leise underline"
      >
        {zahlen ? t("objektzahlen.tabelleZu") : t("objektzahlen.tabelleAuf")}
      </button>

      {zahlen && (
        <table className="mt-2 w-full text-sm">
          <tbody>
            {[...reihe.punkte].reverse().map((p) => (
              <tr key={p.datum} className="border-t border-linie">
                <td className="py-1 text-leise">{datum(p.datum)}</td>
                <td className="py-1 text-end font-semibold tabular-nums">
                  {zahl(p.wert, sprache)}{reihe.einheit ? ` ${reihe.einheit}` : ""}
                  {/* Beim Verbrauch steht der Tageswert im Balken, die
                      Menge auf dem Zaehler. Beides gehoert nebeneinander. */}
                  {p.menge !== undefined && p.tage !== undefined && (
                    <span className="ms-2 text-xs font-normal text-leise">
                      {t("objektzahlen.mengeUeberTage", {
                        menge: zahl(p.menge, sprache),
                        einheit: reihe.einheit ?? "",
                        count: p.tage
                      })}
                    </span>
                  )}
                  {p.ausserhalb && (
                    <span className="ms-2 text-xs text-red-700">{t("objektzahlen.ausserhalb")}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function zahl(n: number, sprache: string): string {
  return new Intl.NumberFormat(sprache, { maximumFractionDigits: 2 }).format(n);
}

/** Eine Zahl mit Ueberschrift. Fehlt sie, steht ein Strich und keine Null. */
function Zahlfeld({ titel, wert, einheit, sprache }: {
  titel: string; wert: number | null; einheit: string | null; sprache: string;
}) {
  return (
    <div>
      <dt className="text-[11px] text-leise">{titel}</dt>
      <dd className="text-sm font-bold tabular-nums">
        {wert === null ? "—" : zahl(wert, sprache)}
        {wert !== null && einheit ? <span className="font-normal"> {einheit}</span> : null}
      </dd>
    </div>
  );
}

/**
 * Balken fuer Verbrauch, in Menge je Tag.
 *
 * Frueher stand hier die Menge je Ablesung. Ueber ein langes Wochenende wurde
 * der Balken hoeher, ohne dass jemand mehr Wasser gebraucht hatte, und genau
 * die Frage, die man an so ein Bild stellt, war damit nicht zu beantworten.
 *
 * Die gestrichelte Linie ist der uebliche Tagesverbrauch an diesem Objekt.
 * Ohne sie muesste jeder Betrachter im Kopf mitteln, um zu wissen, ob ein
 * Balken hoch ist.
 */
function Balken({ punkte, basis, sprache }: {
  punkte: Punkt[]; basis: number | null; sprache: string;
}) {
  const hoch = Math.max(...punkte.map((p) => p.wert), basis ?? 0, 0) || 1;
  const flaeche = BREITE - RAND.links - RAND.rechts;
  const breite = flaeche / punkte.length;
  const balken = Math.max(4, breite - 4); // 2px Luft je Seite, wie vorgesehen
  const nutz = HOEHE - RAND.oben - RAND.unten;
  const y = (wert: number) => HOEHE - RAND.unten - (wert / hoch) * nutz;

  return (
    <svg viewBox={`0 0 ${BREITE} ${HOEHE}`} className="w-full" role="img"
      aria-label={punkte.map((p) => `${p.datum}: ${zahl(p.wert, sprache)}`).join(", ")}>
      <line x1={0} y1={HOEHE - RAND.unten} x2={BREITE} y2={HOEHE - RAND.unten}
        stroke={FLAECHE} strokeWidth={1} />
      {punkte.map((p, i) => {
        const h = Math.max(2, (p.wert / hoch) * nutz);
        const x = RAND.links + i * breite + (breite - balken) / 2;
        /* Rot, wo der Balken das Uebliche deutlich uebersteigt. Die Farbe sagt
           dasselbe wie die Linie, nur ohne dass man vergleichen muss. */
        const viel = basis !== null && basis > 0 && p.wert >= basis * 2;
        return (
          <rect
            key={p.datum}
            x={x} y={HOEHE - RAND.unten - h}
            width={balken} height={h}
            rx={4}
            fill={viel ? ROT : AKZENT}
          />
        );
      })}
      {basis !== null && basis > 0 && (
        <line
          x1={0} y1={y(basis)} x2={BREITE} y2={y(basis)}
          stroke={LEISE} strokeWidth={1} strokeDasharray="4 3"
        />
      )}
      <Hoechstwert hoch={hoch} sprache={sprache} />
      <Achse punkte={punkte} sprache={sprache} />
    </svg>
  );
}

/**
 * Der obere Wert der Skala.
 *
 * Ein Balkenbild ohne Massstab sagt nur, welcher Balken der hoechste ist. Erst
 * die Zahl oben sagt, wie viel das ist.
 */
function Hoechstwert({ hoch, sprache }: { hoch: number; sprache: string }) {
  return (
    <text x={2} y={RAND.oben + 2} fontSize={10} fill={LEISE}>
      {zahl(hoch, sprache)}
    </text>
  );
}

/**
 * Linie fuer Messwerte, mit dem Zielbereich als Band im Hintergrund.
 *
 * Das Band beantwortet die Frage, die man bei "pH 7,6" wirklich hat: ist das
 * gut. Ohne den Zielbereich ist die Zahl bedeutungslos.
 */
function Linie({ reihe, sprache }: { reihe: Reihe; sprache: string }) {
  const { punkte, ziel } = reihe;
  const werte = punkte.map((p) => p.wert);
  const grenzen = [
    ...werte,
    ...(ziel?.min !== null && ziel?.min !== undefined ? [ziel.min] : []),
    ...(ziel?.max !== null && ziel?.max !== undefined ? [ziel.max] : [])
  ];
  let min = Math.min(...grenzen);
  let max = Math.max(...grenzen);
  if (max - min < 0.0001) { min -= 1; max += 1; }
  const luft = (max - min) * 0.15;
  min -= luft;
  max += luft;

  const flaeche = BREITE - RAND.links - RAND.rechts;
  const nutz = HOEHE - RAND.oben - RAND.unten;
  const x = (i: number) => RAND.links + (punkte.length === 1 ? flaeche / 2 : (i / (punkte.length - 1)) * flaeche);
  const y = (w: number) => RAND.oben + (1 - (w - min) / (max - min)) * nutz;

  const pfad = punkte.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.wert).toFixed(1)}`).join(" ");

  return (
    <svg viewBox={`0 0 ${BREITE} ${HOEHE}`} className="w-full" role="img"
      aria-label={punkte.map((p) => `${p.datum}: ${zahl(p.wert, sprache)}`).join(", ")}>
      {ziel && ziel.min !== null && ziel.max !== null && (
        <rect
          x={0} y={y(ziel.max)}
          width={BREITE} height={Math.max(1, y(ziel.min) - y(ziel.max))}
          fill={FLAECHE}
        />
      )}
      <line x1={0} y1={HOEHE - RAND.unten} x2={BREITE} y2={HOEHE - RAND.unten}
        stroke={FLAECHE} strokeWidth={1} />
      <path d={pfad} fill="none" stroke={AKZENT} strokeWidth={2}
        strokeLinecap="round" strokeLinejoin="round" />
      {punkte.map((p, i) => (
        <circle
          key={p.datum}
          cx={x(i)} cy={y(p.wert)} r={4}
          fill={p.ausserhalb ? ROT : AKZENT}
          stroke="#FFFFFF" strokeWidth={2}
        />
      ))}
      {/* Ober- und Untergrenze der Skala. Ohne sie sagt die Kurve nur, dass
          sich etwas bewegt hat, und nicht um wie viel. */}
      <text x={2} y={RAND.oben + 2} fontSize={10} fill={LEISE}>{zahl(max, sprache)}</text>
      <text x={2} y={HOEHE - RAND.unten - 2} fontSize={10} fill={LEISE}>
        {zahl(min, sprache)}
      </text>
      <Achse punkte={punkte} sprache={sprache} />
    </svg>
  );
}

/** Erstes und letztes Datum. Mehr passt auf einem Telefon nicht lesbar hin. */
function Achse({ punkte, sprache }: { punkte: Punkt[]; sprache: string }) {
  const kurz = (d: string) =>
    new Date(d).toLocaleDateString(sprache, { day: "2-digit", month: "2-digit" });
  return (
    <>
      <text x={2} y={HOEHE - 4} fontSize={10} fill={LEISE}>{kurz(punkte[0].datum)}</text>
      {punkte.length > 1 && (
        <text x={BREITE - 2} y={HOEHE - 4} fontSize={10} fill={LEISE} textAnchor="end">
          {kurz(punkte[punkte.length - 1].datum)}
        </text>
      )}
    </>
  );
}
